import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Loading placeholder for a TanStack-table-shaped surface per
 * [ADR 0021](../../../../docs/adr/0021-frontend-feedback-loop-polish.md).
 * Renders the same table chrome (header row + N body rows) as the real
 * table will, with `<Skeleton>` bars in each cell — so the swap when
 * data arrives is jank-free.
 *
 * Used while a query is in its initial load (`isLoading` — first fetch
 * with no cached data). Background refetches don't show the skeleton;
 * the existing table content stays put and the new data slots in.
 *
 * `columns` should be the real column labels so the header row matches.
 * Cell widths cycle through a few values to avoid the placeholder
 * looking like a single uniform block.
 */
interface TableSkeletonProps {
  readonly columns: readonly string[];
  readonly rows?: number;
}

const CELL_WIDTHS = ['w-16', 'w-36', 'w-24', 'w-28', 'w-20'] as const;

export function TableSkeleton({ columns, rows = 5 }: TableSkeletonProps) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="border-border bg-card overflow-hidden rounded-md border"
    >
      <table className="w-full text-sm">
        <thead className="bg-muted/50 border-border border-b">
          <tr>
            {columns.map((label) => (
              <th
                key={label}
                className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, rowIdx) => (
            <tr key={`row-${String(rowIdx)}`} className="border-border border-b last:border-b-0">
              {columns.map((label, colIdx) => (
                <td key={label} className="px-4 py-3">
                  <Skeleton className={cn('h-4', CELL_WIDTHS[colIdx % CELL_WIDTHS.length])} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
